import { useEffect, useState } from 'react'
import { useAppDispatch } from '@/store'

import { useConfigureModalController, useModalController, useCloseModal } from '@/atoms'
import { validateDriverDelete } from '@/features/domain/driver'

type ModalState = 'ready' | 'submitting' | 'invalid'
type ModalData = {
  driverIds: string[]
  deletableDriverIds: string[]
  validationResult?: uui.domain.server.rm.DeleteDriverValidationResults
}
type ModalProps = {
  driverIds: string[]
}

function getDeletableDriverIds(
  driverIds: string[],
  validationResult?: uui.domain.server.rm.DeleteDriverValidationResults,
) {
  if (!validationResult) return driverIds

  const driverIdsToDelete = new Set<string>()

  for (const driverId in validationResult) {
    const result = validationResult[driverId]

    if (result.valid) {
      driverIdsToDelete.add(driverId)
    }
  }

  return Array.from(driverIdsToDelete)
}

export const modalId = 'deleteDriverModal'

export const useController = () => useModalController<ModalData, ModalState>(modalId)

export const useConfigureController = ({ driverIds }: ModalProps) => {
  const close = useCloseModal()
  const dispatch = useAppDispatch()

  const [options] = useState(() => ({
    createInitialState: () => ({
      close,
      status: 'pending' as ModalState,
      data: {
        driverIds,
        deletableDriverIds: [],
      },
    }),
  }))

  const ctrl = useConfigureModalController<ModalData, ModalState>(modalId, options)
  const { update } = ctrl

  useEffect(() => {
    async function validateDriversForDeletion(driverIds: string[]) {
      const result = await dispatch(validateDriverDelete(driverIds))

      if (validateDriverDelete.rejected.match(result)) {
        throw new Error(result.payload?.message ?? 'Internal error')
      }

      return result.payload
    }

    try {
      validateDriversForDeletion(driverIds).then(validationResult => {
        const allValidDrivers = Object.values(validationResult).every(r => r.valid)

        const deletableDriverIds = getDeletableDriverIds(driverIds, validationResult)
        update({
          status: allValidDrivers ? 'ready' : 'invalid',
          data: {
            driverIds,
            validationResult,
            deletableDriverIds,
          },
        })
      })
    } catch (error) {
      update({
        status: 'invalid',
        data: {
          driverIds,
          validationResult: undefined,
          deletableDriverIds: [],
        },
      })
    }
  }, [driverIds, dispatch, update])

  return ctrl
}
